/**
 * The engine's instances: the domain's `PartInstance` / `PieceInstance`
 * (PLAN §5.2, `@norna/domain/instances.js`) plus what playout needs on them.
 *
 * Added to a piece instance: where its piece **started** (`startPartId`,
 * `startSegmentId` — Sofie's `startPartId`/`startSegmentId`, needed to decide
 * which lifespans reach a later part). Added to a part instance: its pieces,
 * its calculated take timings (Sofie's `partPlayoutTimings`, fixed at take),
 * the anchor a queued adlib part was inserted after, and whether taking it
 * consumes the queued segment.
 *
 * Extensions of the domain schemas, so the engine-state stream still parses
 * every domain field with the domain's own validators.
 */
import { partInstanceSchema, pieceInstanceSchema } from '@norna/domain/instances.js';
import { ulidSchema } from '@norna/domain/primitives.js';
import { z } from 'zod';
import { partCalculatedTimingsSchema } from './partTimings.js';

export const enginePieceInstanceSchema = pieceInstanceSchema.extend({
  /** The part the piece was defined in (null: an adlib with no part). */
  startPartId: ulidSchema.nullable(),
  startSegmentId: ulidSchema.nullable(),
});
export type EnginePieceInstance = z.infer<typeof enginePieceInstanceSchema>;

export const enginePartInstanceSchema = partInstanceSchema.extend({
  pieces: z.array(enginePieceInstanceSchema),
  /** Calculated at take (Sofie: "once the part has been taken this should not be recalculated"). */
  partTimings: partCalculatedTimingsSchema.nullable(),
  /** A queued adlib part: the definition part it was inserted after, for next-selection. */
  insertedAfterPartId: ulidSchema.nullable(),
  /** Taking it clears the queued segment (Sofie's `consumesQueuedSegmentId`). */
  consumesQueuedSegmentId: z.boolean(),
});
export type EnginePartInstance = z.infer<typeof enginePartInstanceSchema>;

/** Where the instance's part sits for order purposes: its own id, or the part a queued adlib part followed. */
export function anchorPartId(instance: EnginePartInstance): string {
  return instance.insertedAfterPartId ?? instance.partId;
}
